import { useCallback } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {
  Audience,
  BulkResolveSessionsRowResult,
  BulkResolveSessionsRowStatus,
  BulkResolveSessionsSpeakerProposal,
  SessionTypeConfig,
} from "@event-planner/shared";
import {
  headersOf,
  sessionImportSpec,
  sessionTypeImportSpec,
  validateRows,
  type ParsedSessionImportRow,
  type ParsedSessionTypeImportRow,
} from "@event-planner/shared/imports";
import { readExcelRows } from "../../utils/excel";
import { dateTimeLocalToTimestamp } from "../../utils/timestamps";
import {
  ResolveAndImportDialog,
  type ProposalRow,
} from "../../components/imports/ResolveAndImportDialog";
import { type BulkImportResultBucket } from "../../components/BulkImportResultTable";
import {
  bulkCreateSessions,
  bulkResolveSessions,
} from "../../services/sessionService";
import { updateEvent } from "../../services/eventService";

interface ExcelSessionImportDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** IANA timezone , local-time strings get converted to UTC against this. */
  timezone: string;
  /** Existing event audiences, used to resolve Session Types defaultAudience names. */
  audiences: Audience[];
  /** Fires after a non-zero import run completes so the parent can refetch. */
  onImported?: () => void;
}

type SessionProposal = ProposalRow<
  ParsedSessionImportRow,
  BulkResolveSessionsRowResult
>;

type WriteResult = Awaited<ReturnType<typeof bulkCreateSessions>>;

export function defaultIncludeForSessionStatus(
  status: BulkResolveSessionsRowStatus | "invalid",
): { include: boolean; locked: boolean } {
  switch (status) {
    case "new":
      return { include: true, locked: false };
    case "duplicate_title":
    case "unknown_category":
    case "unknown_location":
    case "unknown_audience":
    case "audience_required":
    case "unresolved_speakers":
    case "invalid_time":
    case "failed":
    case "invalid":
      return { include: false, locked: true };
  }
}

/**
 * Bulk Sessions import with full per-cell preview. Each row is resolved
 * server-side via `bulkResolveSessions` before any write. The reviewing
 * phase shows the resolved sessionType / location / audience / speakers
 * per row and surfaces identity matches for every speaker email (event
 * roster person / known person not on this event / no match).
 *
 * Local-time -> UTC conversion happens admin-side (timezone-aware via
 * `dateTimeLocalToTimestamp`). The same converted ISO is sent to both
 * the resolve and write callables so the duplicate-title check fires
 * against the correct startTime.
 */
export function ExcelSessionImportDialog({
  open,
  onClose,
  eventId,
  timezone,
  audiences,
  onImported,
}: ExcelSessionImportDialogProps) {
  const buildCallableRow = useCallback(
    (r: ParsedSessionImportRow) => {
      const startTs = dateTimeLocalToTimestamp(r.startTimeLocal, timezone);
      const endTs = dateTimeLocalToTimestamp(r.endTimeLocal, timezone);
      return {
        title: r.title,
        description: r.description,
        sessionTypeName: r.sessionTypeName,
        startTime: startTs.toDate().toISOString(),
        endTime: endTs.toDate().toISOString(),
        locationLabel: r.locationLabel,
        audience: r.audience,
        speakerEmails: r.speakerEmails,
      };
    },
    [timezone],
  );

  const resolveRows = useCallback(
    async (rows: ParsedSessionImportRow[]) => {
      const response = await bulkResolveSessions({
        eventId,
        rows: rows.map(buildCallableRow),
      });
      return response.results;
    },
    [eventId, buildCallableRow],
  );

  const writeRows = useCallback(
    async (rows: ParsedSessionImportRow[]) => {
      return bulkCreateSessions({
        eventId,
        rows: rows.map(buildCallableRow),
      });
    },
    [eventId, buildCallableRow],
  );

  const prepareFile = useCallback(
    async (file: File) => {
      const parsedSessionTypes = await readSessionTypesSheet(file);
      if (parsedSessionTypes === null) return;
      if (parsedSessionTypes.length === 0) return;

      const audienceIdByName = new Map(
        audiences
          .filter((audience) => audience.name)
          .map((audience) => [audience.name!.toLowerCase(), audience.id]),
      );
      const sessionTypes = parsedSessionTypes.map((row) =>
        toSessionTypeConfig(row, audienceIdByName),
      );

      await updateEvent(eventId, {
        "config.sessionTypes": sessionTypes,
      });
    },
    [audiences, eventId],
  );

  const renderProposalCard = useCallback(
    (proposal: SessionProposal, onToggle: () => void) => (
      <ProposalCard proposal={proposal} onToggle={onToggle} />
    ),
    [],
  );

  return (
    <ResolveAndImportDialog<
      ParsedSessionImportRow,
      BulkResolveSessionsRowResult,
      WriteResult
    >
      open={open}
      onClose={onClose}
      onImported={onImported}
      title="Import Sessions from Excel"
      entityNoun={{ singular: "Session", plural: "Sessions" }}
      pickerHelpText={
        <>
          Upload an Excel file (.xlsx) with one row per session. Required:{" "}
          <strong>title</strong>, <strong>sessionTypeName</strong>,{" "}
          <strong>startTime</strong>, <strong>endTime</strong>. Times are in
          event-local format (YYYY-MM-DD HH:MM, timezone:{" "}
          <strong>{timezone}</strong>). Categories, locations, groups, and
          emails are resolved server-side; you'll see the resolved values
          per row before anything is saved.
        </>
      }
      testIdSlug="session"
      spec={sessionImportSpec}
      templateFileName="session-import-template.xlsx"
      sheetName="Sessions"
      prepareFile={prepareFile}
      resolveRows={resolveRows}
      writeRows={writeRows}
      defaultIncludeForResolution={(resolution) =>
        defaultIncludeForSessionStatus(resolution?.status ?? "invalid")
      }
      renderProposalCard={renderProposalCard}
      buildResultBuckets={({ writeResult, includedProposals, skippedProposals }) =>
        buildSessionBuckets(writeResult, includedProposals, skippedProposals)
      }
      importedAnythingFromResult={(result) => result.summary.created > 0}
    />
  );
}

async function readSessionTypesSheet(
  file: File,
): Promise<ParsedSessionTypeImportRow[] | null> {
  let headers: string[];
  let rawRows: Record<string, string | undefined>[];
  try {
    const result = await readExcelRows<Record<string, string | undefined>>(
      file,
      { sheetName: sessionTypeImportSpec.sheetName },
    );
    headers = result.headers;
    rawRows = result.rows;
  } catch (err) {
    if (
      err instanceof Error &&
      err.message.includes(`Sheet "${sessionTypeImportSpec.sheetName}"`)
    ) {
      return null;
    }
    throw err;
  }

  if (!headersMatch(headers, headersOf(sessionTypeImportSpec))) {
    return null;
  }

  const parsed = validateRows(sessionTypeImportSpec, rawRows);
  const invalid = parsed.filter((row) => row.status === "invalid");
  if (invalid.length > 0) {
    const first = invalid[0];
    throw new Error(
      `Session Types sheet row ${first.lineNumber}: ${first.message ?? "invalid row"}`,
    );
  }
  return parsed
    .filter((row) => row.status === "pending")
    .map((row) => row.data);
}

function headersMatch(actualHeaders: string[], expectedHeaders: string[]): boolean {
  const actual = actualHeaders.map((header) => header.trim());
  const expected = expectedHeaders.map((header) => header.trim());
  return (
    actual.length === expected.length &&
    actual.every((header, index) => header === expected[index])
  );
}

function toSessionTypeConfig(
  row: ParsedSessionTypeImportRow,
  audienceIdByName: ReadonlyMap<string, string>,
): SessionTypeConfig {
  return {
    id: slugify(row.name),
    name: row.name,
    backgroundColor: row.backgroundColor,
    textColor: row.textColor,
    iconName: row.iconName,
    defaultAudienceId: row.defaultAudience
      ? audienceIdByName.get(row.defaultAudience.toLowerCase()) ?? null
      : null,
  };
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// ---------------------------------------------------------------------------
// Per-row proposal card (session-specific layout)
// ---------------------------------------------------------------------------

function ProposalCard({
  proposal,
  onToggle,
}: {
  proposal: SessionProposal;
  onToggle: () => void;
}) {
  const { resolution, parsed, include, includeLocked, lineNumber } = proposal;

  if (!resolution) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5 }}
        data-testid={`proposal-row-${lineNumber}`}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            checked={false}
            disabled
            data-testid={`proposal-include-${lineNumber}`}
          />
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {parsed.data.title || <em>(missing title)</em>}
            </Typography>
            <Typography variant="caption" color="error.main">
              {parsed.message ?? "Format error"}
            </Typography>
          </Stack>
          <Chip size="small" label="Format error" color="error" />
        </Stack>
      </Paper>
    );
  }

  const audienceLabel = (() => {
    const a = resolution.proposal?.audience;
    if (!a) return null;
    if (a.source === "named") return `Audience: ${a.name}`;
    if (a.source === "personal")
      return `Audience: ${a.displayName} (${a.pending ? "pending, " : ""}just this person)`;
    return "Audience: category default";
  })();

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, opacity: includeLocked ? 0.85 : 1 }}
      data-testid={`proposal-row-${lineNumber}`}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Checkbox
          checked={include}
          disabled={includeLocked}
          onChange={onToggle}
          data-testid={`proposal-include-${lineNumber}`}
        />
        <Stack spacing={0.75} flex={1} minWidth={0}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {resolution.title}
            </Typography>
            <StatusChip status={resolution.status} />
          </Stack>

          <Stack spacing={0.25}>
            {resolution.proposal?.sessionType && (
              <Typography variant="caption" color="text.secondary">
                Category: {resolution.proposal.sessionType.name}
              </Typography>
            )}
            {resolution.proposal?.location && (
              <Typography variant="caption" color="text.secondary">
                Location: {resolution.proposal.location.label}
              </Typography>
            )}
            {audienceLabel && (
              <Typography variant="caption" color="text.secondary">
                {audienceLabel}
              </Typography>
            )}
          </Stack>

          {resolution.proposal && resolution.proposal.speakerProposals.length > 0 && (
            <>
              <Divider />
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Speakers ({resolution.proposal.speakerProposals.length})
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {resolution.proposal.speakerProposals.map((p, i) => (
                    <SpeakerChip key={`${p.email}-${i}`} proposal={p} />
                  ))}
                </Stack>
              </Stack>
            </>
          )}

          {resolution.message && resolution.status !== "new" && (
            <Typography variant="caption" color="warning.main">
              {resolution.message}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function SpeakerChip({
  proposal,
}: {
  proposal: BulkResolveSessionsSpeakerProposal;
}) {
  const tooltipParts: string[] = [proposal.email];
  let color: "success" | "warning" | "default" | "error";
  let label: string;
  let photoURL: string | null = null;

  switch (proposal.source) {
    case "roster_member":
      color = "success";
      label = proposal.displayName || proposal.email;
      photoURL = proposal.photoURL;
      tooltipParts.push(
        proposal.pending ? "Pending roster person" : "Claimed roster person",
      );
      if (proposal.priorEventNames.length > 0) {
        tooltipParts.push(
          `Prior events: ${proposal.priorEventNames.join(", ")}`,
        );
      }
      break;
    case "not_on_roster":
      color = "warning";
      label = proposal.displayName || proposal.email;
      photoURL = proposal.photoURL;
      tooltipParts.push(
        "Person exists, but is not on this event roster. Add them via the Users page first.",
      );
      if (proposal.priorEventNames.length > 0) {
        tooltipParts.push(
          `Prior events: ${proposal.priorEventNames.join(", ")}`,
        );
      }
      break;
    case "no_match":
      color = "error";
      label = proposal.email;
      tooltipParts.push("No person exists for this email yet.");
      break;
  }

  return (
    <Tooltip
      title={
        <Box sx={{ whiteSpace: "pre-line" }}>{tooltipParts.join("\n")}</Box>
      }
    >
      <Chip
        size="small"
        avatar={photoURL ? <Avatar src={photoURL} /> : undefined}
        label={label}
        color={color}
        variant={proposal.source === "no_match" ? "outlined" : "filled"}
      />
    </Tooltip>
  );
}

function StatusChip({ status }: { status: BulkResolveSessionsRowStatus }) {
  switch (status) {
    case "new":
      return <Chip size="small" label="New, ready" color="success" />;
    case "duplicate_title":
      return <Chip size="small" label="Duplicate title" color="warning" />;
    case "unknown_category":
      return <Chip size="small" label="Unknown category" color="warning" />;
    case "unknown_location":
      return <Chip size="small" label="Unknown location" color="warning" />;
    case "unknown_audience":
      return <Chip size="small" label="Unknown audience" color="warning" />;
    case "audience_required":
      return <Chip size="small" label="Audience required" color="warning" />;
    case "unresolved_speakers":
      return <Chip size="small" label="Unresolved speakers" color="warning" />;
    case "invalid_time":
      return <Chip size="small" label="Invalid time" color="error" />;
    case "failed":
      return <Chip size="small" label="Failed" color="error" />;
  }
}

// ---------------------------------------------------------------------------
// Done-phase result bucketing
// ---------------------------------------------------------------------------

interface ResultRow {
  lineNumber: number;
  title: string;
  status:
    | "success"
    | "duplicate"
    | "ref_issue"
    | "skipped"
    | "failed"
    | "invalid";
  message: string | null;
}

function buildSessionBuckets(
  writeResult: WriteResult | null,
  includedProposals: SessionProposal[],
  skippedProposals: SessionProposal[],
): BulkImportResultBucket[] {
  const writeRows: ResultRow[] = includedProposals.map((p, i) => {
    const r = writeResult?.results[i];
    if (!r) {
      return {
        lineNumber: p.lineNumber,
        title: p.parsed.data.title,
        status: "failed",
        message: "Skipped at write phase",
      };
    }
    const status: ResultRow["status"] =
      r.status === "created"
        ? "success"
        : r.status === "duplicate_title"
          ? "duplicate"
          : r.status === "unknown_category" ||
              r.status === "unknown_location" ||
              r.status === "unknown_audience" ||
              r.status === "audience_required" ||
              r.status === "unresolved_emails" ||
              r.status === "invalid_time"
            ? "ref_issue"
            : "failed";
    return {
      lineNumber: p.lineNumber,
      title: p.parsed.data.title,
      status,
      message: r.message ?? null,
    };
  });

  const skipRows: ResultRow[] = skippedProposals.map((p) => {
    if (!p.resolution) {
      return {
        lineNumber: p.lineNumber,
        title: p.parsed.data.title || "(no title)",
        status: "invalid" as const,
        message: p.parsed.message ?? "Format error",
      };
    }
    return {
      lineNumber: p.lineNumber,
      title: p.resolution.title,
      status: "skipped" as const,
      message: p.resolution.message ?? null,
    };
  });

  const all = [...writeRows, ...skipRows].sort(
    (a, b) => a.lineNumber - b.lineNumber,
  );
  const success = all.filter((r) => r.status === "success");
  const duplicate = all.filter((r) => r.status === "duplicate");
  const refIssue = all.filter((r) => r.status === "ref_issue");
  const skipped = all.filter((r) => r.status === "skipped");
  const failed = all.filter(
    (r) => r.status === "failed" || r.status === "invalid",
  );

  return [
    {
      id: "success",
      label: "Successfully Added",
      color: "success",
      rows: success.map((r) => ({
        id: `s-${r.lineNumber}`,
        primary: r.title,
        secondary: `Row ${r.lineNumber}`,
      })),
    },
    {
      id: "duplicate",
      label: "Already Exists",
      color: "warning",
      description:
        "A session with this title and start time already exists in this event.",
      rows: duplicate.map((r) => ({
        id: `d-${r.lineNumber}`,
        primary: r.title,
        secondary: `Row ${r.lineNumber}`,
      })),
    },
    {
      id: "ref-issues",
      label: "Reference Issues",
      color: "warning",
      description:
        "Categories, locations, audiences, or speaker emails that don't exist or aren't on this event roster yet. Add them via the admin pages, then re-import.",
      rows: refIssue.map((r) => ({
        id: `r-${r.lineNumber}`,
        primary: r.title,
        secondary: `Row ${r.lineNumber}: ${r.message ?? "unresolved"}`,
      })),
    },
    {
      id: "skipped",
      label: "Skipped",
      color: "default",
      description:
        "Rows the organizer chose not to import (locked off in the review phase).",
      rows: skipped.map((r) => ({
        id: `k-${r.lineNumber}`,
        primary: r.title,
        secondary: `Row ${r.lineNumber}${r.message ? `: ${r.message}` : ""}`,
      })),
    },
    {
      id: "failed",
      label: "Failed",
      color: "error",
      description: "Validation errors or callable rejections.",
      rows: failed.map((r) => ({
        id: `f-${r.lineNumber}`,
        primary: r.title,
        secondary: `Row ${r.lineNumber}: ${r.message ?? "unknown error"}`,
      })),
    },
  ];
}
